<template>
  <div class="catalog-view">
    <div class="catalog-view__breadcrumbs">
      <router-link to="/">Главная</router-link>
      <span>/</span>
      <router-link to="/catalog">Каталог</router-link>
      <span v-if="currentCategory">/</span>
      <span v-if="currentCategory">{{ currentCategory.name }}</span>
    </div>

    <UiSection
      class="catalog-view__section"
      :title="currentCategory ? currentCategory.name : 'Каталог товаров'"
      :subtitle="sectionSubtitle"
    >
      <template #actions>
        <div class="catalog-view__view-toggle">
          <button
            type="button"
            :class="['catalog-view__toggle', { 'is-active': viewMode === 'grid' }]"
            @click="viewMode = 'grid'"
          >
            Сетка
          </button>
          <button
            type="button"
            :class="['catalog-view__toggle', { 'is-active': viewMode === 'list' }]"
            @click="viewMode = 'list'"
          >
            Список
          </button>
        </div>
      </template>

      <UiCard class="catalog-view__toolbar" data-tutorial="filters">
        <div class="catalog-view__filters">
          <div class="catalog-view__field catalog-view__field--search">
            <label class="catalog-view__label">Поиск</label>
            <div class="catalog-view__search-wrapper">
              <input
                v-model="searchQuery"
                type="text"
                aria-label="Поиск по названию или артикулу"
                class="catalog-view__search-input"
                placeholder="Название, артикул..."
                @input="onSearchInput"
                @keyup.enter="performSearch"
              />
              <button
                v-if="searchQuery"
                type="button"
                class="catalog-view__search-clear"
                @click="clearSearch"
              >
                ✕
              </button>
            </div>
          </div>
          <div class="catalog-view__field">
            <label class="catalog-view__label">Категория</label>
            <UiSelect
              v-model="filters.selectedCategory"
              :options="categorySelectOptions"
              @update:modelValue="onCategoryChange"
            />
          </div>
          <div class="catalog-view__field">
            <label class="catalog-view__label">Сортировка</label>
            <UiSelect
              v-model="sortBy"
              :options="sortSelectOptions"
              @update:modelValue="onSortChange"
            />
          </div>
          <div class="catalog-view__field catalog-view__field--checkbox">
            <label class="catalog-view__label">&nbsp;</label>
            <label class="catalog-view__checkbox">
              <input v-model="inStock" type="checkbox" @change="onInStockChange" />
              <span class="catalog-view__checkbox-box" aria-hidden="true" />
              <span class="catalog-view__checkbox-label">В наличии</span>
            </label>
          </div>
          <div v-if="isAdvancedFiltersEnabled" class="catalog-view__field catalog-view__field--btn">
            <label class="catalog-view__label">&nbsp;</label>
            <UiButton variant="secondary" @click="showAdvancedFilters = !showAdvancedFilters">
              Фильтры {{ hasAdvancedFilters ? `(${activeFiltersCount})` : '' }}
            </UiButton>
          </div>
        </div>
      </UiCard>

      <CatalogAttributeFilters
        :attributes="dynamicAttributeFilters"
        :value-filters="attributeValueFilters"
        :range-filters="attributeRangeFilters"
        :boolean-filters="attributeBooleanFilters"
        :has-selections="hasAttributeSelections"
        @clear="clearAttributeFilters"
        @update:range-min="updateAttributeRangeMin"
        @update:range-max="updateAttributeRangeMax"
        @update:boolean="setAttributeBooleanFilter"
        @toggle-option="toggleAttributeOption"
      />

      <div
        :class="[
          'catalog-view__content',
          { 'catalog-view__content--single': !showAdvancedFilters || !isAdvancedFiltersEnabled },
        ]"
      >
        <UiCard
          v-if="showAdvancedFilters && isAdvancedFiltersEnabled"
          class="catalog-view__advanced"
        >
          <div class="catalog-view__section-header">
            <h3>Расширенные фильтры</h3>
            <button
              v-if="hasAdvancedFilters"
              type="button"
              class="catalog-view__link"
              @click="clearAdvancedFilters"
            >
              Сбросить
            </button>
          </div>

          <div class="catalog-view__advanced-section">
            <h4>Производители</h4>
            <div class="catalog-view__checkbox-grid">
              <label
                v-for="manufacturer in manufacturers"
                :key="manufacturer.id"
                class="catalog-view__checkbox"
              >
                <input
                  type="checkbox"
                  :value="manufacturer.id"
                  :checked="filters.manufacturers.includes(manufacturer.id)"
                  @change="toggleManufacturer(manufacturer.id)"
                />
                <span class="catalog-view__checkbox-box" aria-hidden="true" />
                <span class="catalog-view__checkbox-label">{{ manufacturer.name }}</span>
              </label>
            </div>
          </div>

          <div class="catalog-view__advanced-section">
            <h4>Цена, ₽</h4>
            <div class="catalog-view__price-range">
              <div>
                <label class="catalog-view__label">От</label>
                <UiInput
                  :model-value="String(filters.priceRange[0])"
                  input-filter="decimal"
                  @update:modelValue="updateMinPrice"
                />
              </div>
              <div>
                <label class="catalog-view__label">До</label>
                <UiInput
                  :model-value="String(filters.priceRange[1])"
                  input-filter="decimal"
                  @update:modelValue="updateMaxPrice"
                />
              </div>
            </div>
          </div>

          <div class="catalog-view__advanced-section">
            <h4>Поставщики</h4>
            <UiSelect
              v-model="filters.selectedSupplier"
              :options="supplierSelectOptions"
              @update:modelValue="onSupplierChange"
            />
          </div>
        </UiCard>

        <div class="catalog-view__main">
          <div v-if="hasActiveFilters" class="catalog-view__active-filters">
            <span class="catalog-view__muted">Активные фильтры:</span>
            <div class="catalog-view__badges">
              <button
                v-for="chip in activeFilterChips"
                :key="chip.key"
                type="button"
                class="catalog-view__chip"
                @click="removeActiveFilter(chip)"
              >
                <span>{{ chip.label }}</span>
                <span class="catalog-view__chip-remove" aria-hidden="true">×</span>
              </button>
              <button type="button" class="catalog-view__link" @click="clearFilters">
                Сбросить все
              </button>
            </div>
          </div>

          <UiCard v-if="loading" class="catalog-view__state">Загрузка товаров...</UiCard>

          <div v-else-if="products.length > 0" class="catalog-view__products">
            <div v-if="viewMode === 'grid'" class="catalog-view__grid">
              <ProductCard v-for="product in products" :key="product.id" :product="product" />
            </div>
            <div v-else class="catalog-view__list">
              <ProductCard
                v-for="product in products"
                :key="product.id"
                :product="product"
                variant="horizontal"
              />
            </div>
            <div v-if="totalPages > 1" class="catalog-view__pagination">
              <UiPager
                :page="currentPage"
                :totalPages="totalPages"
                :show-pages="true"
                @prev="prevPage"
                @next="nextPage"
                @page="goToPage"
              />
            </div>
          </div>

          <UiCard v-else class="catalog-view__state">
            <div class="catalog-view__empty-title">Товары не найдены</div>
            <div class="catalog-view__muted">Попробуйте изменить параметры поиска или фильтры</div>
            <UiButton variant="secondary" @click="clearFilters"> Показать все товары </UiButton>
          </UiCard>
        </div>
      </div>
    </UiSection>
  </div>
</template>

<script setup lang="ts">
import ProductCard from '@/modules/catalog/components/ProductCard.vue'
import CatalogAttributeFilters from '@/modules/catalog/components/CatalogAttributeFilters.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiInput from '@/shared/ui/UiInput.vue'
import UiPager from '@/shared/ui/UiPager.vue'
import UiSection from '@/shared/ui/UiSection.vue'
import UiSelect from '@/shared/ui/UiSelect.vue'
import { useCatalogView } from '@/modules/catalog/composables/useCatalogView'

const {
  activeFilterChips,
  activeFiltersCount,
  attributeBooleanFilters,
  attributeRangeFilters,
  attributeValueFilters,
  categorySelectOptions,
  clearAdvancedFilters,
  clearAttributeFilters,
  clearFilters,
  clearSearch,
  currentCategory,
  currentPage,
  dynamicAttributeFilters,
  filters,
  goToPage,
  hasActiveFilters,
  hasAdvancedFilters,
  hasAttributeSelections,
  inStock,
  isAdvancedFiltersEnabled,
  loading,
  manufacturers,
  nextPage,
  onCategoryChange,
  onInStockChange,
  onSearchInput,
  onSortChange,
  onSupplierChange,
  performSearch,
  prevPage,
  products,
  removeActiveFilter,
  searchQuery,
  setAttributeBooleanFilter,
  sectionSubtitle,
  showAdvancedFilters,
  sortBy,
  sortSelectOptions,
  supplierSelectOptions,
  toggleAttributeOption,
  toggleManufacturer,
  totalPages,
  updateAttributeRangeMax,
  updateAttributeRangeMin,
  updateMaxPrice,
  updateMinPrice,
  viewMode,
} = useCatalogView()
</script>

<style scoped src="../styles/CatalogView.css"></style>
